/**
 * Owner/manager home (BA-01/02/03, AD-03/04). KPI row + revenue/membership trend,
 * revenue-by-kind, membership-status donut, and an occupancy heatmap.
 */
import {
  getMembershipAnalytics,
  getOccupancyDensity,
  getProfitLoss,
  getRevenueByKind,
} from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CalendarCheck, TrendingUp, Users } from 'lucide-react';
import { type ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DonutChart,
  type HeatCell,
  OccupancyHeatmap,
  RankedBarChart,
  seriesColor,
  TrendChart,
} from '../../components/charts/index.js';
import { KpiCard } from '../../components/ui/KpiCard.js';
import { ChartCard, PageHeader } from '../../components/ui/Layout.js';
import { Money, useFormatters, useLocale } from '../../components/ui/Money.js';
import { useAuth } from '../../lib/auth.js';
import { useLocations } from '../../lib/locations.js';
import { supabase } from '../../lib/supabase.js';

const STATUS_COLOR: Record<string, string> = {
  active: '#22C55E',
  frozen: '#38BDF8',
  pending: '#94A3B8',
  expired: '#EF4444',
  cancelled: '#64748B',
};

function startOfTodayIso(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

export function DashboardPage(): ReactNode {
  const { t } = useTranslation();
  const locale = useLocale();
  const fmt = useFormatters();
  const { claims } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  const { locations, activeLocationId } = useLocations();
  const heatmapLocationId = activeLocationId ?? locations[0]?.id ?? null;

  const pl = useQuery({
    queryKey: ['profit_loss', tenantId],
    queryFn: () => getProfitLoss(supabase, tenantId),
    enabled: !!tenantId,
  });
  const rbk = useQuery({
    queryKey: ['revenue_by_kind', tenantId],
    queryFn: () => getRevenueByKind(supabase, tenantId),
    enabled: !!tenantId,
  });
  const ma = useQuery({
    queryKey: ['membership_analytics', tenantId],
    queryFn: () => getMembershipAnalytics(supabase, tenantId),
    enabled: !!tenantId,
  });
  const checkinsToday = useQuery({
    queryKey: ['checkins_today', tenantId],
    enabled: !!tenantId,
    queryFn: async (): Promise<number> => {
      const { count, error } = await supabase
        .from('checkins')
        .select('id', { count: 'exact', head: true })
        .gte('checked_in_at', startOfTodayIso())
        .in('result', ['granted', 'granted_override', 'allowed_warn_overdue']);
      if (error) throw error;
      return count ?? 0;
    },
  });
  const density = useQuery({
    queryKey: ['density', tenantId, heatmapLocationId],
    enabled: !!tenantId && !!heatmapLocationId,
    queryFn: async (): Promise<HeatCell[]> => {
      const { data, error } = await getOccupancyDensity(supabase, {
        tenantId,
        locationId: heatmapLocationId as string,
        granularity: 'hourly',
      });
      if (error) throw error;
      const byCell = new Map<string, number>();
      for (const row of (data ?? []) as { bucket_start: string; count: number }[]) {
        const d = new Date(row.bucket_start);
        const day = (d.getDay() + 6) % 7; // Mon=0..Sun=6
        const hour = d.getHours();
        const k = `${day}-${hour}`;
        byCell.set(k, (byCell.get(k) ?? 0) + row.count);
      }
      return [...byCell.entries()].map(([k, value]) => {
        const [day, hour] = k.split('-');
        return { day: Number(day), hour: Number(hour), value };
      });
    },
  });

  // ── Derived ────────────────────────────────────────────────────────────────
  const months = useMemo(() => [...(pl.data ?? [])].reverse(), [pl.data]); // ascending
  const current = pl.data?.[0];
  const prev = pl.data?.[1];
  const revenueDelta = useMemo(() => {
    if (!current || !prev || prev.income === 0) return null;
    const pct = ((current.income - prev.income) / Math.abs(prev.income)) * 100;
    return { value: `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`, positive: pct >= 0 };
  }, [current, prev]);

  const trendData = useMemo(
    () =>
      months.slice(-8).map((m) => ({
        label: fmt.date(m.month_bucket, { month: 'short' }),
        [t('analytics.income')]: Math.round(m.income / 100),
        [t('analytics.profit')]: Math.round(m.profit / 100),
      })),
    [months, fmt, t],
  );

  const latestKindBucket = useMemo(() => {
    const rows = rbk.data ?? [];
    const latest = rows.reduce<string | null>(
      (acc, r) => (acc === null || r.month_bucket > acc ? r.month_bucket : acc),
      null,
    );
    return rows.filter((r) => r.month_bucket === latest);
  }, [rbk.data]);

  const kindData = useMemo(
    () =>
      latestKindBucket.map((r, i) => ({
        label: t(`analytics.kind.${r.kind}`, { defaultValue: r.kind }),
        value: Math.round(r.total / 100),
        color: seriesColor(i),
      })),
    [latestKindBucket, t],
  );

  const statusData = useMemo(
    () =>
      (ma.data ?? [])
        .filter((r) => r.member_count > 0)
        .map((r) => ({
          label: t(`memberships.status.${r.status}`, { defaultValue: r.status }),
          value: r.member_count,
          color: STATUS_COLOR[r.status] ?? '#94A3B8',
        })),
    [ma.data, t],
  );

  // Localized Mon..Sun short weekday labels (2024-01-01 was a Monday).
  const dayLabels = useMemo(
    () =>
      Array.from({ length: 7 }, (_, i) => fmt.date(new Date(2024, 0, 1 + i), { weekday: 'short' })),
    [fmt],
  );

  const activeMembers = (ma.data ?? []).find((r) => r.status === 'active')?.member_count ?? 0;
  const totalMembers = (ma.data ?? []).reduce((s, r) => s + r.member_count, 0);
  const expiringSoon = (ma.data ?? []).reduce((s, r) => s + (r.expiring_30d ?? 0), 0);

  return (
    <div className="grid gap-5">
      <PageHeader title={t('nav.dashboard')} subtitle={t('analytics.dashboardSubtitle')} />

      {/* KPI row */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label={t('analytics.activeMembers')}
          value={fmt.number(activeMembers)}
          icon={Users}
          tone="success"
          hint={t('analytics.ofTotal', { total: fmt.number(totalMembers) })}
          loading={ma.isLoading}
        />
        <KpiCard
          label={t('analytics.monthlyRevenue')}
          value={<Money minor={current?.income ?? 0} className="text-3xl" />}
          icon={TrendingUp}
          tone="primary"
          delta={revenueDelta}
          spark={months.slice(-8).map((m) => Math.round(m.income / 100))}
          loading={pl.isLoading}
        />
        <KpiCard
          label={t('analytics.expiringSoon')}
          value={fmt.number(expiringSoon)}
          icon={AlertTriangle}
          tone="warning"
          hint={t('analytics.next30Days')}
          loading={ma.isLoading}
        />
        <KpiCard
          label={t('analytics.checkinsToday')}
          value={fmt.number(checkinsToday.data ?? 0)}
          icon={CalendarCheck}
          tone="info"
          loading={checkinsToday.isLoading}
        />
      </div>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ChartCard
          title={t('analytics.revenueTrend')}
          subtitle={t('analytics.last8Months')}
          loading={pl.isLoading}
          empty={trendData.length === 0 ? t('common.noData') : undefined}
          className="xl:col-span-2"
        >
          <TrendChart
            data={trendData}
            series={[
              { key: t('analytics.income'), name: t('analytics.income'), color: '#F97316' },
              { key: t('analytics.profit'), name: t('analytics.profit'), color: '#22C55E' },
            ]}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>

        <ChartCard
          title={t('analytics.membershipSplit')}
          loading={ma.isLoading}
          empty={statusData.length === 0 ? t('common.noData') : undefined}
        >
          <DonutChart
            data={statusData}
            centerValue={fmt.number(totalMembers)}
            centerLabel={t('members.title')}
          />
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <ChartCard
          title={t('analytics.revenueByKind')}
          subtitle={t('analytics.currentMonth')}
          loading={rbk.isLoading}
          empty={kindData.length === 0 ? t('common.noData') : undefined}
        >
          <RankedBarChart
            data={kindData}
            formatValue={(v) => new Intl.NumberFormat(locale).format(v)}
          />
        </ChartCard>

        <ChartCard
          title={t('analytics.occupancyHeatmap')}
          subtitle={t('analytics.last30Days')}
          loading={density.isLoading}
          empty={
            !heatmapLocationId
              ? t('analytics.noLocation')
              : (density.data ?? []).length === 0
                ? t('common.noData')
                : undefined
          }
          height={300}
          className="xl:col-span-2"
        >
          <OccupancyHeatmap cells={density.data ?? []} dayLabels={dayLabels} />
        </ChartCard>
      </div>
    </div>
  );
}
